"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  DEFAULT_BRANDING,
  buildBrandCssVars,
  parseTenantBranding,
  type TenantBranding,
} from "@growwithai/shared";

// Branding tenant di client (workstream B F3).
// Sumber utama: window.__TENANT__ (di-inject middleware/worker ke <head> —
// tersedia SEBELUM hydration → init sinkron, tanpa FOUC).
// Fallback dev (next dev tanpa worker): fetch /api/public/tenant-config.
// Fallback akhir: DEFAULT_BRANDING.

declare global {
  interface Window {
    __TENANT__?: unknown;
    // Prefetch konten beranda ('/') disuntik edge (lihat resolveTenantHomeData
    // di apps/web/functions/_lib/tenant.ts) — dikonsumsi page.tsx/StorePageClient.tsx
    // sebagai initial state agar HTML awal tak kosong menunggu fetch client.
    __TENANT_HOME__?: { path: '/api/public/landing' | '/api/public/store'; body: unknown } | null;
  }
}

/** null = flag belum diketahui (dev/fallback) → UI menampilkan semua modul. */
export type TenantFeatures = Record<string, boolean> | null;

export interface TenantNavItemOverride {
  label?: string;
  hidden?: boolean;
}
/** Tautan menu kustom siswa (di luar item bawaan). Mirror navCustomLinkSchema. */
export interface TenantNavCustomLink {
  id: string;
  type: "internal" | "external";
  label: string;
  target: string;
  hidden?: boolean;
}
export interface TenantNavOverrides {
  user: Record<string, TenantNavItemOverride>;
  admin: Record<string, TenantNavItemOverride>;
  /** Override per-SEKSI sidebar (rename/sembunyikan grup) — key = id seksi. */
  user_sections: Record<string, TenantNavItemOverride>;
  admin_sections: Record<string, TenantNavItemOverride>;
  /** Tautan kustom siswa yang ditambahkan admin. */
  customLinks: TenantNavCustomLink[];
}

const EMPTY_NAV: TenantNavOverrides = {
  user: {},
  admin: {},
  user_sections: {},
  admin_sections: {},
  customLinks: [],
};

/** Istilah UI per tenant (app_settings 'terminology'); key absen = istilah default. */
export type TenantTerminology = Record<string, string>;

/** Elemen dashboard member yang bisa disembunyikan tenant. */
export interface TenantDashboardConfig {
  show_premium_cta?: boolean;
  show_recommendations?: boolean;
}

/** Mode monetisasi tenant; absen di __TENANT__ → 'membership'. */
export type TenantMonetizationMode = "membership" | "per_class" | "mixed" | "digital_product";

/** Pengumuman banner atas dashboard siswa (app_settings 'announcement_config'). */
export interface TenantAnnouncement {
  enabled: boolean;
  message: string;
  link_url?: string;
  link_label?: string;
}

const EMPTY_ANNOUNCEMENT: TenantAnnouncement = { enabled: false, message: "" };

/** Copy halaman login/register per tenant (app_settings 'auth_config'). */
export interface TenantAuthConfig {
  login_headline?: string;
  login_subtitle?: string;
  register_headline?: string;
  register_subtitle?: string;
  registration_mode?: "open" | "invite_only" | "closed";
  referral_sources?: string[];
}

interface TenantState {
  branding: TenantBranding;
  features: TenantFeatures;
  navOverrides: TenantNavOverrides;
  landingEnabled: boolean;
  storeEnabled: boolean;
  storeAsHome: boolean;
  authConfig: TenantAuthConfig;
  communityUrl: string | null;
  terminology: TenantTerminology;
  dashboardConfig: TenantDashboardConfig;
  monetizationMode: TenantMonetizationMode;
  feeRateBps: number;
  demoMode: boolean;
  announcement: TenantAnnouncement;
}

const TenantContext = createContext<TenantState>({
  branding: DEFAULT_BRANDING,
  features: null,
  navOverrides: EMPTY_NAV,
  landingEnabled: false,
  storeEnabled: false,
  storeAsHome: false,
  authConfig: {},
  communityUrl: null,
  terminology: {},
  dashboardConfig: {},
  monetizationMode: "membership",
  feeRateBps: 0,
  demoMode: false,
  announcement: EMPTY_ANNOUNCEMENT,
});

function extractFeatures(raw: unknown): TenantFeatures {
  if (raw && typeof raw === "object" && "features" in raw) {
    const f = (raw as { features?: unknown }).features;
    if (f && typeof f === "object") return f as Record<string, boolean>;
  }
  return null;
}

function extractNavOverrides(raw: unknown): TenantNavOverrides {
  if (raw && typeof raw === "object" && "nav_overrides" in raw) {
    const n = (raw as { nav_overrides?: unknown }).nav_overrides;
    if (n && typeof n === "object") {
      const o = n as Partial<TenantNavOverrides>;
      return {
        user: o.user && typeof o.user === "object" ? o.user : {},
        admin: o.admin && typeof o.admin === "object" ? o.admin : {},
        user_sections:
          o.user_sections && typeof o.user_sections === "object" ? o.user_sections : {},
        admin_sections:
          o.admin_sections && typeof o.admin_sections === "object" ? o.admin_sections : {},
        customLinks: Array.isArray(o.customLinks) ? o.customLinks : [],
      };
    }
  }
  return EMPTY_NAV;
}

function extractTerminology(raw: unknown): TenantTerminology {
  if (raw && typeof raw === "object" && "terminology" in raw) {
    const t = (raw as { terminology?: unknown }).terminology;
    if (t && typeof t === "object" && !Array.isArray(t)) {
      const out: TenantTerminology = {};
      for (const [k, v] of Object.entries(t as Record<string, unknown>)) {
        if (typeof v === "string" && v.trim() !== "") out[k] = v;
      }
      return out;
    }
  }
  return {};
}

function extractDashboardConfig(raw: unknown): TenantDashboardConfig {
  if (raw && typeof raw === "object" && "dashboard_config" in raw) {
    const d = (raw as { dashboard_config?: unknown }).dashboard_config;
    if (d && typeof d === "object" && !Array.isArray(d)) {
      const o = d as Record<string, unknown>;
      const bool = (v: unknown) => (typeof v === "boolean" ? v : undefined);
      return {
        show_premium_cta: bool(o.show_premium_cta),
        show_recommendations: bool(o.show_recommendations),
      };
    }
  }
  return {};
}

function extractMonetizationMode(raw: unknown): TenantMonetizationMode {
  if (raw && typeof raw === "object" && "monetization_mode" in raw) {
    const m = (raw as { monetization_mode?: unknown }).monetization_mode;
    if (m === "membership" || m === "per_class" || m === "mixed" || m === "digital_product") return m;
  }
  return "membership";
}

function extractBoolFlag(raw: unknown, key: string): boolean {
  return !!(
    raw &&
    typeof raw === "object" &&
    (raw as Record<string, unknown>)[key] === true
  );
}

function extractLandingEnabled(raw: unknown): boolean {
  return !!(
    raw &&
    typeof raw === "object" &&
    (raw as { landing_enabled?: unknown }).landing_enabled === true
  );
}

function extractAuthConfig(raw: unknown): TenantAuthConfig {
  if (raw && typeof raw === "object" && "auth_config" in raw) {
    const a = (raw as { auth_config?: unknown }).auth_config;
    if (a && typeof a === "object" && !Array.isArray(a)) {
      const o = a as Record<string, unknown>;
      const str = (v: unknown) => (typeof v === "string" && v.trim() !== "" ? v : undefined);
      const sources = Array.isArray(o.referral_sources)
        ? o.referral_sources.filter((s): s is string => typeof s === "string" && s.trim() !== "")
        : undefined;
      const mode =
        o.registration_mode === "invite_only" || o.registration_mode === "closed"
          ? o.registration_mode
          : undefined;
      return {
        login_headline: str(o.login_headline),
        login_subtitle: str(o.login_subtitle),
        register_headline: str(o.register_headline),
        register_subtitle: str(o.register_subtitle),
        registration_mode: mode,
        referral_sources: sources && sources.length > 0 ? sources : undefined,
      };
    }
  }
  return {};
}

function extractCommunityUrl(raw: unknown): string | null {
  if (raw && typeof raw === "object" && "community_whatsapp_url" in raw) {
    const u = (raw as { community_whatsapp_url?: unknown }).community_whatsapp_url;
    if (typeof u === "string" && u.trim() !== "") return u;
  }
  return null;
}

function extractFeeRateBps(raw: unknown): number {
  if (raw && typeof raw === "object" && "fee_rate_bps" in raw) {
    const v = (raw as { fee_rate_bps?: unknown }).fee_rate_bps;
    if (typeof v === "number" && Number.isFinite(v) && v >= 0) return v;
  }
  return 0;
}

function extractAnnouncement(raw: unknown): TenantAnnouncement {
  if (raw && typeof raw === "object" && "announcement" in raw) {
    const a = (raw as { announcement?: unknown }).announcement;
    if (a && typeof a === "object" && !Array.isArray(a)) {
      const o = a as Record<string, unknown>;
      const str = (v: unknown) => (typeof v === "string" && v.trim() !== "" ? v : undefined);
      return {
        enabled: o.enabled === true,
        message: typeof o.message === "string" ? o.message : "",
        link_url: str(o.link_url),
        link_label: str(o.link_label),
      };
    }
  }
  return EMPTY_ANNOUNCEMENT;
}

function extractDemoMode(raw: unknown): boolean {
  return !!(
    raw &&
    typeof raw === "object" &&
    (raw as { demo_mode?: unknown }).demo_mode === true
  );
}

function toState(raw: unknown): TenantState {
  return {
    branding: parseTenantBranding(raw),
    features: extractFeatures(raw),
    navOverrides: extractNavOverrides(raw),
    landingEnabled: extractLandingEnabled(raw),
    storeEnabled: extractBoolFlag(raw, "store_enabled"),
    storeAsHome: extractBoolFlag(raw, "store_as_home"),
    authConfig: extractAuthConfig(raw),
    communityUrl: extractCommunityUrl(raw),
    terminology: extractTerminology(raw),
    dashboardConfig: extractDashboardConfig(raw),
    monetizationMode: extractMonetizationMode(raw),
    feeRateBps: extractFeeRateBps(raw),
    demoMode: extractDemoMode(raw),
    announcement: extractAnnouncement(raw),
  };
}

function initialState(): TenantState {
  if (typeof window !== "undefined" && window.__TENANT__) {
    return toState(window.__TENANT__);
  }
  return {
    branding: DEFAULT_BRANDING,
    features: null,
    navOverrides: EMPTY_NAV,
    landingEnabled: false,
    storeEnabled: false,
    storeAsHome: false,
    authConfig: {},
    communityUrl: null,
    terminology: {},
    dashboardConfig: {},
    monetizationMode: "membership",
    feeRateBps: 0,
    demoMode: false,
    announcement: EMPTY_ANNOUNCEMENT,
  };
}

/**
 * Muat ulang konfigurasi tenant tanpa memuat ulang halaman.
 *
 * Context terpisah, BUKAN field di TenantState: TenantState dibangun `toState()`
 * dari respons API, jadi menyelipkan fungsi ke dalamnya berarti tiap pemanggil
 * `toState` harus menyediakan callback yang tidak ada hubungannya dengan data.
 *
 * Dipakai setelah admin menyimpan branding, supaya warna & nama brand baru
 * langsung efektif. Aman karena <style id="tenant-theme-client"> di bawah
 * dimiliki React dan dirender ulang tiap `state.branding` berubah — selector
 * `:root:root` miliknya menang cascade tanpa bergantung urutan.
 */
const TenantRefreshContext = createContext<() => Promise<void>>(async () => {});

/** Refresher branding/config tenant. Panggil setelah menyimpan branding. */
export function useTenantRefresh(): () => Promise<void> {
  return useContext(TenantRefreshContext);
}

const TENANT_CONFIG_URL = `${process.env.NEXT_PUBLIC_API_URL ?? ""}/api/public/tenant-config`;

export default function TenantProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<TenantState>(initialState);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(TENANT_CONFIG_URL, {
        headers: { accept: "application/json" },
        credentials: "include",
        cache: "no-store",
      });
      if (!res.ok) return;
      const body = (await res.json()) as { success?: boolean; data?: unknown };
      if (!body?.success || !body.data) return;
      window.__TENANT__ = body.data;
      setState(toState(body.data));
    } catch {
      // Gagal muat ulang bukan alasan merusak layar; nilai lama tetap dipakai.
    }
  }, []);

  // CSS var brand di-maintain React (client-only) sebagai jaring pengaman.
  // Edge (functions/_lib/tenant.ts) menyuntik <style id="tenant-theme"> di <head>
  // untuk anti-FOUC pre-hydration, TAPI node mentah itu bisa hilang saat App
  // Router merekonsiliasi <head>/navigasi SPA → tanpa penopang, --brand-* balik
  // ke default hijau di globals.css. Style React-owned ini SELALU ada pasca-mount
  // dan re-render tiap `state.branding` berubah (mis. fetch tenant-config di dev),
  // sehingga bertahan lintas navigasi. Client-only (hydrated) agar tak render
  // nilai DEFAULT (hijau) saat SSR — di server window.__TENANT__ belum ada.
  // Selector `:root:root` dari buildBrandCssVars menang cascade tanpa lihat urutan.

  useEffect(() => {
    if (typeof window === "undefined" || window.__TENANT__) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/public/tenant-config", {
          headers: { accept: "application/json" },
        });
        if (!res.ok) return;
        const body = (await res.json()) as { success?: boolean; data?: unknown };
        if (!cancelled && body?.success && body.data) {
          window.__TENANT__ = body.data;
          setState(toState(body.data));
        }
      } catch {
        // offline / API mati → tetap DEFAULT_BRANDING
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <TenantContext.Provider value={state}>
      <TenantRefreshContext.Provider value={refresh}>
        {hydrated && (
          <style
            id="tenant-theme-client"
            dangerouslySetInnerHTML={{ __html: buildBrandCssVars(state.branding) }}
          />
        )}
        {children}
      </TenantRefreshContext.Provider>
    </TenantContext.Provider>
  );
}

/** Branding tenant aktif — selalu terdefinisi (fallback DEFAULT_BRANDING). */
export function useTenant(): TenantBranding {
  return useContext(TenantContext).branding;
}

/** Flag modul tenant; null = belum diketahui → tampilkan semua (mode GWA/dev). */
export function useTenantFeatures(): TenantFeatures {
  return useContext(TenantContext).features;
}

/** Biaya layanan buyer-borne (bps). 0 = single-tenant/dev (fee tidak berlaku). */
export function useServiceFeeBps(): number {
  return useContext(TenantContext).feeRateBps;
}

/** Override label/hidden menu per tenant (dari app_settings 'nav_overrides'). */
export function useTenantNavOverrides(): TenantNavOverrides {
  return useContext(TenantContext).navOverrides;
}

/** true bila landing tenant di `/` diaktifkan admin tenant. */
export function useTenantLandingEnabled(): boolean {
  return useContext(TenantContext).landingEnabled;
}

/** true bila etalase /store aktif (store_config.enabled). */
export function useTenantStoreEnabled(): boolean {
  return useContext(TenantContext).storeEnabled;
}

/** true bila /store menggantikan '/' sebagai beranda (store_config.as_home). */
export function useTenantStoreAsHome(): boolean {
  return useContext(TenantContext).storeAsHome;
}

/** Copy login/register per tenant; objek kosong bila belum diset. */
export function useTenantAuthConfig(): TenantAuthConfig {
  return useContext(TenantContext).authConfig;
}

/** URL grup komunitas tenant; null = tidak diset → sembunyikan banner komunitas. */
export function useTenantCommunityUrl(): string | null {
  return useContext(TenantContext).communityUrl;
}

/**
 * Fungsi penerjemah istilah tenant — `term(key, fallback)` mengembalikan
 * istilah kustom dari app_settings 'terminology', atau fallback bila absen.
 */
export function useTerm(): (key: string, fallback: string) => string {
  const terminology = useContext(TenantContext).terminology;
  return useCallback((key: string, fallback: string) => terminology[key] ?? fallback, [terminology]);
}

/** Konfigurasi elemen dashboard member; field absen = tampil (default). */
export function useDashboardConfig(): TenantDashboardConfig {
  return useContext(TenantContext).dashboardConfig;
}

/** Mode monetisasi tenant aktif; default 'membership'. */
export function useMonetizationMode(): TenantMonetizationMode {
  return useContext(TenantContext).monetizationMode;
}

/**
 * true bila mode ini punya tingkatan membership sungguhan. 'per_class' dan
 * 'digital_product' sama-sama TIDAK punya tingkatan membership — dipakai untuk
 * menyembunyikan CTA/copy upgrade-premium di kedua mode itu sekaligus, alih-alih
 * mengecek 'per_class' saja seperti sebelum mode 'digital_product' ada.
 */
export function hasMembershipTier(mode: TenantMonetizationMode): boolean {
  return mode === "membership" || mode === "mixed";
}

/** true bila tenant berjalan sebagai instance demo (baca-saja) → tampilkan kredensial demo. */
export function useDemoMode(): boolean {
  return useContext(TenantContext).demoMode;
}

/** Pengumuman banner atas dashboard siswa (app_settings 'announcement_config'). */
export function useTenantAnnouncement(): TenantAnnouncement {
  return useContext(TenantContext).announcement;
}

/** true bila item ber-feature `key` boleh tampil untuk tenant ini. */
// Fitur BERBAYAR (premium admin) — bila status flag tak diketahui (features===null,
// mis. tenant-config gagal di-resolve di edge → NEUTRAL_TENANT), JANGAN tampilkan
// (fail-closed) supaya fitur premium seperti affiliate tak bocor ke tenant free.
// Fitur inti gratis (community) tetap fail-open agar tak hilang saat loading.
const PREMIUM_FEATURE_KEYS = new Set([
  "affiliate",
  "gamification",
  "webinar",
  "api",
  "tryout_analytics",
  "custom_domain",
  "ai_tools",
  "email_sender_domain",
]);

export function isFeatureVisible(features: TenantFeatures, key?: string): boolean {
  if (!key) return true;          // item inti tanpa gating
  if (features === null) return !PREMIUM_FEATURE_KEYS.has(key); // unknown → sembunyikan yang berbayar
  return features[key] === true;
}
